import React from 'react';

/** Stroke icons from the UI refresh design. Decorative: the button around them carries the label. */
type P = { size?: number; color?: string; width?: number };

export const TickIcon: React.FC<P> = ({ size = 20, color = '#FFFFFF', width = 2.6 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

export const CrossIcon: React.FC<P> = ({ size = 20, color = '#FFFFFF', width = 2.6 }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round">
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </svg>
);

export const DuplicateIcon: React.FC<P> = ({ size = 18, color = '#FFFFFF' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
  </svg>
);

export const DeleteIcon: React.FC<P> = ({ size = 18, color = '#FFFFFF' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round">
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </svg>
);

export const ImageIcon: React.FC<P> = ({ size = 28, color = 'rgba(255,255,255,0.55)' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.6}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="10" r="1.8" />
    <path d="M21 16l-5-5-8 8" />
  </svg>
);

export const PlayIcon: React.FC<P> = ({ size = 16, color = '#2E333B' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);

export const DownloadIcon: React.FC<P> = ({ size = 16, color = '#2E333B' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4v11M7 10.5l5 5 5-5M5 20h14" />
  </svg>
);

/** Move to another project */
export const MoveIcon: React.FC<P> = ({ size = 16, color = '#2E333B' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M10 13h6M13.5 10.5L16 13l-2.5 2.5" />
  </svg>
);

export const ChevronIcon: React.FC<P & { up?: boolean }> = ({ size = 16, color = '#2E333B', up = false }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <path d={up ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
  </svg>
);

/** Master · On Ice (archived, cold storage) */
export const IceIcon: React.FC<P> = ({ size = 16, color = '#1E4E8C' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round">
    <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5L12 7l2.5-2.5M9.5 19.5L12 17l2.5 2.5" />
  </svg>
);

export const FileIcon: React.FC<P> = ({ size = 16, color = '#5B616E' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

export const PauseIcon: React.FC<P> = ({ size = 16, color = '#2E333B' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <rect x="6.5" y="5" width="4" height="14" rx="1" />
    <rect x="13.5" y="5" width="4" height="14" rx="1" />
  </svg>
);

/** Screenshot (camera) */
export const CameraIcon: React.FC<P> = ({ size = 16, color = '#2E333B' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round">
    <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);

/** Small solid sort caret for column headings. */
export const SortCaret: React.FC<{ dir: 'asc' | 'desc'; color?: string; size?: number }> = ({ dir, color = 'currentColor', size = 8 }) => (
  <svg aria-hidden="true" width={size} height={size * 0.75} viewBox="0 0 8 6" fill={color} style={{ flex: 'none' }}>
    <path d={dir === 'asc' ? 'M4 0l4 6H0z' : 'M4 6L0 0h8z'} />
  </svg>
);

export const SearchIcon: React.FC<P> = ({ size = 16, color = '#5B616E' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round">
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l4 4" />
  </svg>
);

export const PlusIcon: React.FC<P> =({ size = 16, color = '#FFFFFF' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

/** File information (an "i" in a circle). */
export const InfoIcon: React.FC<P> = ({ size = 16, color = '#2E333B' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v6M12 7.5v.01" />
  </svg>
);

/** Previous / next (image viewer). */
export const ArrowIcon: React.FC<P & { dir?: 'left' | 'right' }> = ({ size = 18, color = '#FFFFFF', dir = 'right' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
    <path d={dir === 'right' ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} />
  </svg>
);

/** One frame back / forward (video player). */
export const FrameStepIcon: React.FC<P & { dir?: 'back' | 'forward' }> = ({ size = 16, color = '#2E333B', dir = 'forward' }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth={2} strokeLinejoin="round">
    {dir === 'forward' ? <path d="M6 6v12l9-6z M18 6v12" /> : <path d="M18 6v12l-9-6z M6 6v12" />}
  </svg>
);
